import { useState, useEffect } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { Text } from '@cyberpion/cyberpion-ui';
import { IconArrow } from 'common/components/Icons/IconArrow/IconArrow';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, IReduxState } from 'app/store';
import Tooltip from '@mui/material/Tooltip';
import { countriesMap } from 'common/constants/countries';
import { Input } from 'common/components/Input/Input';
import RowActionMenu from 'common/components/RowActionMenu/RowActionMenu';
import {
  resetSubsidiaryState,
  setRefreshSelectedSubsidiariesTreeView,
  setSubsidiariesEditMode,
  setSubsidiarySelectedId,
  toggleSubsidiariesPopup
} from 'features/SubsidiariesSlice';
import { setDeletePromptParams } from 'features/GridSlice';
import classNames from 'classnames';
import useAccountPlanLimitations, { PLAN_LIMITATIONS_TYPES } from 'common/hooks/useAccountPlanLimitations';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { resetGroupsState } from 'features/GroupsSlice';
import Checkbox from '@mui/material/Checkbox';
import { makeStyles } from '@material-ui/core/styles';

import './SubsidiariesTreeView.scss';

const iconAlert = (type: string = '') => {
  const color = type === 'warning' ? '#F7991F' : '#F46666';
  return (
    <svg width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1.78555 9.63147L5.77988 2.21342C5.87427 2.03812 6.12573 2.03812 6.22012 2.21342L10.2145 9.63148C10.3041 9.79803 10.1835 10 9.99434 10L2.00566 10C1.8165 10 1.69586 9.79803 1.78555 9.63147Z"
        stroke={color}
      />
      <path d="M6 4.875V7.125" stroke={color} />
      <path d="M6 7.875V8.625" stroke={color} />
    </svg>
  );
};

interface Subsidiary {
  id: number;
  name: string;
  main_domain: string;
  direct_parent?: string;
  subsidiaries?: string[];
  country?: string;
  actions?: any;
  status?: string;
}

interface ITreeNode extends Subsidiary {
  children?: ITreeNode[];
}

interface SubsidiariesTreeViewProps {
  accountName?: string;
}

interface NodeProps {
  node: ITreeNode;
  level: number;
  searchTerm: string;
  isMatch: (node: ITreeNode, term: string) => boolean;
  hasMatchInChildren: (node: ITreeNode, term: string) => boolean;
  setSelectedNodes: (nodes: ITreeNode[]) => void;
  selectedNodes?: ITreeNode[];
}

const useStyles = makeStyles({
  checkboxRoot: {
    color: '#CED0D4 !important',
    padding: '0px !important',
    width: 'auto !important',
    '&.Mui-checked': {
      color: '#1876D2 !important'
    },
    '& .MuiSvgIcon-root': {
      width: '18px !important',
      height: '18px !important'
    }
  }
});

function TreeNode({
  node,
  level,
  searchTerm,
  isMatch,
  hasMatchInChildren,
  setSelectedNodes,
  selectedNodes
}: NodeProps) {
  const [isExpanded, setIsExpanded] = useState(level === 0);
  const hasChildren = node.children && node.children.length > 0;
  const isMatching = searchTerm && isMatch(node, searchTerm);
  const hasMatchingChildren = searchTerm && hasMatchInChildren(node, searchTerm);
  const shouldBeExpanded = searchTerm ? isMatching || hasMatchingChildren : isExpanded;
  const dispatch: AppDispatch = useDispatch();
  const classes = useStyles();

  const handleClick = () => {
    if (hasChildren && level !== 0) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleMenuItemClick = (rowData: any, option: any, type: string, event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (option.id === 'delete') {
      dispatch(setSubsidiarySelectedId(rowData.name));

      dispatch(
        setDeletePromptParams({
          path: 'settings/organization-management/subsidiaries/',
          index: null,
          ids: [rowData.name],
          message: `Are you sure you would like to remove this subsidiary?`,
          type: 'subsidiary'
        })
      );
    }
    if (option.id === 'edit') {
      dispatch(setSubsidiariesEditMode(true));
      dispatch(setSubsidiarySelectedId(rowData.name));
      dispatch(toggleSubsidiariesPopup(true));
    }
  };

  const getAllDescendantNames = (node: ITreeNode): string[] => {
    const names: string[] = [];
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        names.push(child.name);
        names.push(...getAllDescendantNames(child));
      });
    }
    return names;
  };

  const handleOnCheckbox = (node: any) => (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.target.checked) {
      const allDescendantNames = getAllDescendantNames(node);
      const filteredSelectedNodes = selectedNodes?.filter(s => !allDescendantNames.includes(s.name)) || [];
      setSelectedNodes([...filteredSelectedNodes, node]);
    } else {
      const allDescendantNames = getAllDescendantNames(node);
      setSelectedNodes((selectedNodes || []).filter(s => s.name !== node.name && !allDescendantNames.includes(s.name)));
    }
  };

  const countryName = node.country ? ` (${countriesMap[node.country.toUpperCase()]})` : '';

  return (
    <div style={{ marginLeft: `${level * 10}px` }}>
      <div className="tree-node">
        <div
          className={`node-content ${isMatching ? 'highlight-match' : ''} ${hasChildren ? 'has-children' : ''}`}
          onClick={handleClick}
        >
          <div className={classNames('content-wrapper', { 'padding-wrapper': level === 0 })}>
            {hasChildren && level !== 0 && (
              <div className="expand-icon">
                <IconArrow toggleRotate={isExpanded} />
              </div>
            )}
            <div className="node-info">
              <div className="primary-info">
                {level !== 0 && (
                  <Checkbox
                    onChange={handleOnCheckbox(node)}
                    disableRipple
                    classes={{ root: classes.checkboxRoot }}
                    sx={{
                      marginRight: 0.75
                    }}
                  />
                )}

                <Tooltip
                  title={
                    node.name !== 'Account' ? (
                      <div className="subsidiaries-treeview-tt">
                        <Text textSize={16} textColor="#FFF">{`Subsidiary: ${node.name}${countryName}`}</Text>
                        <Text
                          textSize={13}
                          textColor="#FFF"
                          style={{ marginTop: 4 }}
                        >{`Main Domain: ${node.main_domain}`}</Text>
                        <Text textSize={13} textColor="#FFF" style={{ marginTop: 4 }}>{`Status: ${node.status}`}</Text>
                      </div>
                    ) : (
                      ''
                    )
                  }
                  placement="top-start"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: '#4D4C53',
                        color: '#FFFFFF',
                        padding: '3px 6px',
                        marginBottom: '0px !important',
                        '& .MuiTooltip-arrow': {
                          color: '#4D4C53'
                        },
                        border: '1px solid black',
                        borderRadius: 1
                      }
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <span className="node-name">
                      <Text textColor="#6D6D7A">{node.name}</Text>
                    </span>
                    {!!node?.children?.length && (
                      <Text
                        textSize={13}
                        weight={400}
                        textColor="#6D6D7A"
                        style={{ marginRight: 8 }}
                      >{`(${node.children.length})`}</Text>
                    )}
                    <span className="node-domain">{node.main_domain}</span>
                  </div>
                </Tooltip>

                {node.name.toLowerCase() !== 'account' && (
                  <>
                    <span style={{ minWidth: 105 }}>
                      {node.country ? (
                        <span className="country-flag">
                          <ReactCountryFlag countryCode={node.country} svg />
                        </span>
                      ) : null}
                    </span>
                    <span>
                      <Text style={{ whiteSpace: 'nowrap', minWidth: 120 }}>{node.status}</Text>
                    </span>
                    <div className={classNames({ 'row-menu-disabled': !!selectedNodes && !!selectedNodes.length })}>
                      <RowActionMenu
                        rowData={node}
                        itemOnClick={handleMenuItemClick}
                        type="subsidiaries"
                        status={node.actions}
                      />
                    </div>
                    <span style={{ width: 60 }} />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        {level === 0 && isExpanded && (
          <div className="tree-view-header">
            <Text weight={400} textSize={13} textColor="#64748B">
              Details
            </Text>
            <Text weight={400} textSize={13} textColor="#64748B">
              Country
            </Text>
            <Text weight={400} textSize={13} textColor="#64748B">
              Status
            </Text>
            <Text weight={400} textSize={13} textColor="#64748B">
              Actions
            </Text>
          </div>
        )}
        {hasChildren && shouldBeExpanded && (
          <div className="node-children">
            {(node.children || []).map(child => (
              <TreeNode
                key={child.id}
                node={child}
                level={level + 1}
                searchTerm={searchTerm}
                isMatch={isMatch}
                hasMatchInChildren={hasMatchInChildren}
                setSelectedNodes={setSelectedNodes}
                selectedNodes={selectedNodes}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function SubsidiariesTreeView({ accountName }: SubsidiariesTreeViewProps) {
  const [treeData, setTreeData] = useState<ITreeNode | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { count } = useSelector((state: IReduxState) => state.grid);
  const { allSubsidiaries, refreshSelectedSubsidiariesTreeView } = useSelector(
    (state: IReduxState) => state.subsidiaries
  );
  const { planLimitation, limitationsType } = useAccountPlanLimitations('limitSubsidiaries');
  const [limitationsText, setLimitationsText] = useState('');
  const [tooltipContent, setTooltipContent] = useState<any>(null);
  const [actionsMenuOpen, setActionsMenuOpen] = useState<boolean>(false);
  const [selectedNodes, setSelectedNodes] = useState([] as ITreeNode[]);
  const dispatch = useDispatch<any>();

  const flags = useFlags();

  useEffect(() => {
    if (refreshSelectedSubsidiariesTreeView) {
      dispatch(setRefreshSelectedSubsidiariesTreeView(false));
      setSelectedNodes([]);
    }
  }, [refreshSelectedSubsidiariesTreeView, dispatch]);

  useEffect(() => {
    if (limitationsType === PLAN_LIMITATIONS_TYPES.UNLIMITED) {
      setTooltipContent(null);
    }
    if (!!planLimitation && limitationsType === PLAN_LIMITATIONS_TYPES.LIMITED) {
      setLimitationsText(`(${count}/${planLimitation})`);
      let tt: any = null;
      if (count >= planLimitation) {
        tt = (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Text style={{ paddingBottom: 4 }} weight={500}>
              Limit Exceeded
            </Text>
            <Text>Subsidiary grouping creation limit reached (Service Tier Limit).</Text>
            <Text>Remove an existing subsidiary or contact customer success for additional information.</Text>
          </div>
        );
      } else if (count < planLimitation && count >= planLimitation - 2) {
        tt = (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Text style={{ paddingBottom: 4 }} weight={500}>
              {`Group Limit (${count}/${planLimitation})`}
            </Text>
            <Text
              textSize={13}
            >{`You have added ${count} out of ${planLimitation} subsidiaries. If you want more subsidiaries contact customer success for additional information`}</Text>
          </div>
        );
      }
      setTooltipContent(tt);
    }
  }, [planLimitation, limitationsType, count]);

  const isMatch = (node: ITreeNode, term: string) => {
    if (!term) return false;
    return (
      node.name.toLowerCase().includes(term.toLowerCase()) ||
      node.main_domain.toLowerCase().includes(term.toLowerCase())
    );
  };

  const hasMatchInChildren = (node: ITreeNode, term: string): boolean => {
    if (!node.children) return false;
    return node.children.some(child => isMatch(child, term) || hasMatchInChildren(child, term));
  };

  useEffect(() => {
    if (!allSubsidiaries || allSubsidiaries.length === 0) return;

    const rootNode: ITreeNode = {
      id: 0,
      name: accountName || 'Account',
      main_domain: 'All Subsidiaries',
      children: []
    };

    const subsidiaryMap = new Map<string, ITreeNode>();
    allSubsidiaries.forEach((sub: any) => {
      subsidiaryMap.set(sub.name, { ...sub, children: [] });
    });

    allSubsidiaries.forEach((sub: any) => {
      const node = subsidiaryMap.get(sub.name);
      if (!node) return;

      if (!sub.direct_parent) {
        rootNode.children?.push(node);
      } else {
        const parentNode = subsidiaryMap.get(sub.direct_parent);
        if (parentNode) {
          if (!parentNode.children) {
            parentNode.children = [];
          }
          parentNode.children.push(node);
        } else {
          rootNode.children?.push(node);
        }
      }
    });

    setTreeData(rootNode);
  }, [allSubsidiaries, accountName]);

  if (!allSubsidiaries || allSubsidiaries.length === 0) {
    return (
      <div className="subsidiaries-tree-empty">
        <Text textSize={16} weight={500}>
          No subsidiaries found
        </Text>
      </div>
    );
  }

  const cleanText = () => {
    setSearchTerm('');
  };

  const setPopupVisible = (b: boolean) => {
    if (!b) {
      dispatch(resetSubsidiaryState());
      dispatch(resetGroupsState());
    }
    dispatch(toggleSubsidiariesPopup(b));
  };

  const onRemove = () => {
    dispatch(
      setDeletePromptParams({
        category: 'organization-management',
        tab: 'subsidiaries',
        index: null,
        ids: selectedNodes.map(node => node.name),
        message: 'Are you sure you would like to remove the following subsidiaries?',
        type: 'subsidiary',
        isBulk: true
      })
    );
  };

  const handleSelectedNodes = (nodes: ITreeNode[]) => {
    setSelectedNodes(nodes);
  };

  return (
    <div className="subsidiaries-tree">
      <div className="search-container">
        <div className="search-input-wrapper">
          <Input
            icon={'search'}
            type={'text'}
            value={searchTerm}
            placeholder="Search subsidiaries..."
            onChange={value => setSearchTerm(value)}
            onCleanText={cleanText}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button
            className={classNames(
              `btn btn-main btn-subsidiary`,
              { disabled: count >= planLimitation && limitationsType !== PLAN_LIMITATIONS_TYPES.UNLIMITED },
              {
                'regular-border-radius':
                  count < planLimitation - 2 || limitationsType === PLAN_LIMITATIONS_TYPES.UNLIMITED
              }
            )}
            disabled={flags.isGroupsCreationDisabled}
            onClick={() => setPopupVisible(true)}
          >
            {`Add Subsidiary ${limitationsText}`}
          </button>
          {!!tooltipContent && (
            <Tooltip
              title={tooltipContent}
              placement="top-start"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#FFF',
                    color: '#FFFFFF',
                    padding: '6px 8px',
                    fontSize: 13,
                    boxShadow: '0px 4px 14px 0px rgba(0, 0, 0, 0.12) !important',
                    '& .MuiTooltip-arrow': {
                      color: '#4D4C53'
                    },
                    borderRadius: '4px'
                  }
                }
              }}
            >
              <div className={classNames('icon-info-group', { disabled: count >= planLimitation })}>
                {iconAlert(count < planLimitation ? 'warning' : 'error')}
              </div>
            </Tooltip>
          )}
          <span className={`actions-trigger ${actionsMenuOpen ? 'open' : ''}`}>
            <div className="trigger-wrapper" onClick={() => setActionsMenuOpen(!actionsMenuOpen)}>
              Actions
              <IconArrow />
            </div>
            <ul>
              <li className={classNames({ disabled: !selectedNodes.length })} onClick={onRemove}>
                Remove
              </li>
            </ul>
          </span>
        </div>
      </div>

      {treeData && (
        <TreeNode
          node={treeData}
          level={0}
          searchTerm={searchTerm}
          isMatch={isMatch}
          hasMatchInChildren={hasMatchInChildren}
          setSelectedNodes={handleSelectedNodes}
          selectedNodes={selectedNodes}
        />
      )}
    </div>
  );
}
